<script setup lang="ts">
import { computed } from 'vue'
import { useBackendStatus, type DownReason } from '@/composables/useBackendStatus'

const { isDown, lastError } = useBackendStatus()
const isDev = import.meta.env.DEV

interface ReasonConfig {
  icon: string
  title: string
  sub: string
}

const REASON_CONFIG: Record<DownReason, ReasonConfig> = {
  'smtp-down': {
    icon:  '📬',
    title: 'Mailpit non è avviato',
    sub:   'Il servizio SMTP (Mailpit) non risponde. Avvialo con:\n mailpit  oppure  docker run -p 1025:1025 -p 8025:8025 axllent/mailpit',
  },
  'db-down': {
    icon:  '🗄️',
    title: 'Database non raggiungibile',
    sub:   'La connessione al database è caduta. Verifica che MySQL sia attivo.',
  },
  'service-down': {
    icon:  '⚠️',
    title: 'Servizio non disponibile',
    sub:   'Uno o più servizi dipendenti del backend non rispondono.',
  },
  'backend-down': {
    icon:  '🔌',
    title: 'Backend non raggiungibile',
    sub:   'Il server non risponde. Riprovo ogni 3 secondi.',
  },
}

const config = computed<ReasonConfig>(() =>
  REASON_CONFIG[lastError.value?.reason ?? 'backend-down']
)
</script>

<template>
  <Teleport v-if="isDev" to="body">
    <Transition name="overlay-fade">
      <div
        v-if="isDown"
        class="backend-down-overlay"
        role="alertdialog"
        aria-modal="true"
        :aria-label="config.title"
      >
        <div class="backend-down-card">
          <div class="backend-down-icon">{{ config.icon }}</div>

          <h2 class="backend-down-title">{{ config.title }}</h2>

          <p class="backend-down-sub" style="white-space: pre-line">{{ config.sub }}</p>

          <!-- Pannello debug con dettagli tecnici -->
          <div v-if="lastError" class="debug-panel">
            <div class="debug-row">
              <span class="debug-label">Sorgente</span>
              <span class="debug-badge" :class="`badge-${lastError.source}`">{{ lastError.source }}</span>
            </div>
            <div v-if="lastError.downComponents?.length" class="debug-row">
              <span class="debug-label">Componenti DOWN</span>
              <code class="debug-value error-text">{{ lastError.downComponents.join(', ') }}</code>
            </div>
            <div class="debug-row">
              <span class="debug-label">URL</span>
              <code class="debug-value">{{ lastError.url }}</code>
            </div>
            <div v-if="lastError.status" class="debug-row">
              <span class="debug-label">HTTP</span>
              <code class="debug-value">{{ lastError.status }} {{ lastError.statusText }}</code>
            </div>
            <div v-if="lastError.message" class="debug-row">
              <span class="debug-label">Errore</span>
              <code class="debug-value error-text">{{ lastError.message }}</code>
            </div>
            <div class="debug-row">
              <span class="debug-label">Ora</span>
              <code class="debug-value">{{ lastError.timestamp }}</code>
            </div>
          </div>

          <div class="backend-down-dots">
            <span /><span /><span />
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.backend-down-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(0, 0, 0, 0.65);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
}

.backend-down-card {
  background: var(--p-surface-0, #fff);
  border-radius: 1rem;
  padding: 2rem 2.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  box-shadow: 0 24px 64px rgba(0,0,0,.35);
  max-width: 500px;
  width: 92vw;
  text-align: center;
}

.backend-down-icon { font-size: 2.5rem; line-height: 1; }

.backend-down-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--p-text-color, #1e293b);
  margin: 0;
}

.backend-down-sub {
  font-size: 0.85rem;
  color: var(--p-text-muted-color, #64748b);
  margin: 0;
  line-height: 1.6;
  font-family: monospace;
  background: var(--p-surface-100, #f1f5f9);
  padding: 0.5rem 0.75rem;
  border-radius: 0.4rem;
  width: 100%;
  text-align: left;
}

/* Debug panel */
.debug-panel {
  width: 100%;
  background: #0f172a;
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  text-align: left;
}

.debug-row {
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.debug-label {
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  color: #94a3b8;
  min-width: 5.5rem;
  flex-shrink: 0;
}

.debug-value {
  font-size: 0.75rem;
  color: #e2e8f0;
  font-family: monospace;
  word-break: break-all;
}

.error-text { color: #fca5a5; }

.debug-badge {
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  padding: 0.15rem 0.45rem;
  border-radius: 0.25rem;
  color: #fff;
}

.badge-heartbeat { background: #7c3aed; }
.badge-axios     { background: #b45309; }
.badge-poll      { background: #0369a1; }

/* Dots */
.backend-down-dots { display: flex; gap: 0.4rem; }

.backend-down-dots span {
  width: 7px; height: 7px;
  border-radius: 50%;
  background: var(--p-primary-color, #6366f1);
  opacity: 0.3;
  animation: dot-blink 1.2s ease-in-out infinite;
}

.backend-down-dots span:nth-child(2) { animation-delay: 0.2s; }
.backend-down-dots span:nth-child(3) { animation-delay: 0.4s; }

@keyframes dot-blink {
  0%, 80%, 100% { opacity: 0.15; transform: scale(0.85); }
  40%           { opacity: 1;    transform: scale(1.1); }
}

.overlay-fade-enter-active { transition: opacity 0.3s ease; }
.overlay-fade-leave-active { transition: opacity 0.5s ease; }
.overlay-fade-enter-from,
.overlay-fade-leave-to    { opacity: 0; }
</style>
